import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ScanLine,
  BarChart3,
  ShieldCheck,
  LogIn,
  Smartphone,
  Apple,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

// ---------------------------------------------------------------------------
// Public ShelfLife app URL. Update this single constant when the custom
// domain (`app.blueharborlabs.app`) is cut over to production. While the
// subdomain is still being provisioned, the button remains in place but
// the link target can be temporarily flipped to the current Emergent URL
// or to `/login` without touching the rest of the file.
// ---------------------------------------------------------------------------
const APP_LOGIN_URL = "https://app.blueharborlabs.app";

/**
 * Landing / start page.
 *
 * All viewports (desktop, tablet, mobile web): hero → workspace picker → /login,
 * with public marketing sections (Getting Started, install guide, Contact)
 * scrollable below the hero. The Android TWA bypasses this route entirely —
 * its `start_url` in `manifest.json` points at `/login`.
 *
 * Already-authenticated users skip the landing and go straight to the
 * appropriate workspace so the experience never feels redundant on return.
 */
export default function LandingPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stage, setStage] = useState("hero"); // 'hero' | 'workspace'
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    // Defer one tick so the entrance animation actually plays.
    const t = window.setTimeout(() => setRevealed(true), 50);
    return () => window.clearTimeout(t);
  }, []);

  // Bounce authenticated visitors to their workspace.
  if (user && user !== false) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/scan"} replace />;
  }

  const roles = [
    {
      key: "employee",
      label: "Employee",
      blurb: "Scan, count, and remove expiring stock from the floor.",
      Icon: ScanLine,
      testid: "role-employee",
    },
    {
      key: "manager",
      label: "Manager",
      blurb: "Track department performance and approve product requests.",
      Icon: BarChart3,
      testid: "role-manager",
    },
    {
      key: "administrator",
      label: "Administrator",
      blurb: "Manage stores, users, catalog and platform settings.",
      Icon: ShieldCheck,
      testid: "role-administrator",
    },
  ];

  const pickRole = () => {
    // Selecting a role continues into the existing app/login flow.
    nav("/login");
  };

  return (
    <div
      className="min-h-screen bg-paper text-ink flex flex-col"
      data-testid="landing-page"
    >
      <style>{`
        @keyframes sl-rise {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes sl-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes sl-pulse-bar {
          0%   { transform: scaleX(0); transform-origin: left; }
          60%  { transform: scaleX(1); transform-origin: left; }
          61%  { transform-origin: right; }
          100% { transform: scaleX(0); transform-origin: right; }
        }
        .sl-rise-1 { opacity: 0; }
        .sl-revealed .sl-rise-1 { animation: sl-rise 600ms cubic-bezier(.2,.7,.2,1) 80ms forwards; }
        .sl-rise-2 { opacity: 0; }
        .sl-revealed .sl-rise-2 { animation: sl-rise 600ms cubic-bezier(.2,.7,.2,1) 240ms forwards; }
        .sl-rise-3 { opacity: 0; }
        .sl-revealed .sl-rise-3 { animation: sl-rise 600ms cubic-bezier(.2,.7,.2,1) 420ms forwards; }
        .sl-rise-4 { opacity: 0; }
        .sl-revealed .sl-rise-4 { animation: sl-rise 600ms cubic-bezier(.2,.7,.2,1) 600ms forwards; }
        .sl-fade-in { opacity: 0; }
        .sl-revealed .sl-fade-in { animation: sl-fade 700ms ease-out 200ms forwards; }
        .sl-pulse-bar { animation: sl-pulse-bar 2.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .sl-rise-1, .sl-rise-2, .sl-rise-3, .sl-rise-4, .sl-fade-in { opacity: 1 !important; animation: none !important; }
          .sl-pulse-bar { animation: none !important; transform: scaleX(1); transform-origin: left; }
        }
      `}</style>

      {/* Header — sign-in shortcut only on tablet/desktop; mobile skips landing entirely */}
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display font-black text-xl tracking-tight">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-brand" />
          ShelfLife
        </div>
        <a
          href={APP_LOGIN_URL}
          className="inline-flex items-center gap-2 h-10 px-4 sm:px-5 bg-ink text-paper rounded-full font-display font-semibold text-xs uppercase tracking-wider hover:bg-brand transition-colors duration-200"
          data-testid="landing-login-button"
        >
          <LogIn size={14} strokeWidth={2.5} />
          <span className="hidden sm:inline">Log In to ShelfLife</span>
          <span className="sm:hidden">Log In</span>
        </a>
      </header>

      {/* Main content */}
      <main
        className={`flex-1 flex items-center justify-center px-6 sm:px-10 ${revealed ? "sl-revealed" : ""}`}
      >
        {stage === "hero" && (
          <section className="w-full max-w-xl text-center" data-testid="landing-stage-hero">
            <div className="sl-rise-1 inline-flex items-center gap-2 px-3 py-1 border border-border rounded-full font-mono text-[10px] uppercase tracking-wider text-ink/70 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-brand" />
              Retail freshness platform
            </div>

            <h1
              className="sl-rise-2 font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-7xl mb-2"
              data-testid="landing-title"
            >
              Shelf<span className="text-brand">Life</span>
            </h1>

            <div className="sl-fade-in flex justify-center my-5" aria-hidden>
              <div className="relative w-24 h-0.5 bg-secondary overflow-hidden rounded-full">
                <div className="absolute inset-0 bg-brand sl-pulse-bar" />
              </div>
            </div>

            <p
              className="sl-rise-3 font-display text-base sm:text-lg text-ink/70 mb-10 max-w-md mx-auto"
              data-testid="landing-tagline"
            >
              Reduce food waste before it happens.
            </p>

            <div className="sl-rise-4 flex justify-center">
              <button
                type="button"
                onClick={() => setStage("workspace")}
                className="group inline-flex items-center gap-3 h-12 px-7 bg-ink text-paper rounded-full font-display font-semibold text-sm uppercase tracking-wider hover:bg-brand transition-colors duration-200"
                data-testid="landing-get-started-button"
              >
                Get started
                <ArrowRight
                  size={16}
                  strokeWidth={2.5}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </button>
            </div>
          </section>
        )}

        {stage === "workspace" && (
          <section className="w-full max-w-2xl" data-testid="landing-stage-workspace">
            <div className="text-center mb-8">
              <div className="data-sku mb-2">Step 1 of 2</div>
              <h2
                className="font-display font-black tracking-tight text-3xl sm:text-4xl leading-tight"
                data-testid="workspace-title"
              >
                Choose your workspace
              </h2>
              <p className="font-display text-sm text-ink/60 mt-2">
                You can switch later. Selecting a workspace tailors what you see next.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="workspace-grid">
              {roles.map(({ key, label, blurb, Icon, testid }, i) => (
                <button
                  key={key}
                  type="button"
                  onClick={pickRole}
                  className="group text-left p-5 border border-border rounded-sm bg-white hover:border-ink hover:-translate-y-0.5 transition-all duration-200 shadow-sm hover:shadow-md"
                  data-testid={testid}
                  style={{ animationDelay: `${80 + i * 90}ms` }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-9 h-9 rounded-sm bg-secondary group-hover:bg-brand/10 flex items-center justify-center transition-colors">
                      <Icon size={18} strokeWidth={2.5} className="text-ink group-hover:text-brand transition-colors" />
                    </div>
                    <ArrowRight
                      size={14}
                      strokeWidth={2.5}
                      className="text-ink/30 group-hover:text-brand group-hover:translate-x-0.5 transition-all"
                    />
                  </div>
                  <div className="font-display font-black text-lg leading-tight">{label}</div>
                  <div className="font-mono text-[11px] text-ink/60 mt-1 leading-relaxed">{blurb}</div>
                </button>
              ))}
            </div>

            <div className="flex justify-center mt-6">
              <button
                type="button"
                onClick={() => setStage("hero")}
                className="font-mono text-[11px] uppercase tracking-wider text-ink/60 hover:text-ink"
                data-testid="workspace-back-button"
              >
                ← Back
              </button>
            </div>
          </section>
        )}
      </main>

      {/* Getting Started — public marketing section below the hero. */}
      <section
        id="getting-started"
        className="px-6 sm:px-10 py-20 border-t border-border"
        data-testid="getting-started-section"
      >
        <div className="max-w-5xl mx-auto">
          <div className="data-sku mb-3">Onboarding</div>
          <h2
            className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl mb-4"
            data-testid="getting-started-title"
          >
            Getting Started
          </h2>
          <p className="font-display text-base sm:text-lg text-ink/70 max-w-xl mb-12">
            Start using ShelfLife in three simple steps.
          </p>

          {/* Three step cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
            {/* Step 1 */}
            <div
              className="p-6 border border-border rounded-sm bg-white flex flex-col"
              data-testid="getting-started-step-1"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                  <LogIn size={18} strokeWidth={2.5} className="text-ink" />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink/40">
                  Step 1
                </div>
              </div>
              <div className="font-display font-black text-xl leading-tight mb-2">
                Log In to ShelfLife
              </div>
              <p className="font-display text-sm text-ink/70 leading-relaxed mb-5 flex-1">
                Use the Log In to ShelfLife button to access the app.
              </p>
              <a
                href={APP_LOGIN_URL}
                className="group inline-flex items-center gap-2 h-10 px-5 bg-ink text-paper rounded-full font-display font-semibold text-xs uppercase tracking-wider hover:bg-brand transition-colors duration-200 self-start"
                data-testid="getting-started-login-button"
              >
                <LogIn size={14} strokeWidth={2.5} />
                Log In to ShelfLife
                <ArrowRight
                  size={14}
                  strokeWidth={2.5}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </a>
            </div>

            {/* Step 2 */}
            <div
              className="p-6 border border-border rounded-sm bg-white flex flex-col"
              data-testid="getting-started-step-2"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                  <Smartphone size={18} strokeWidth={2.5} className="text-ink" />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink/40">
                  Step 2
                </div>
              </div>
              <div className="font-display font-black text-xl leading-tight mb-2">
                Install ShelfLife on Your Device
              </div>
              <p className="font-display text-sm text-ink/70 leading-relaxed flex-1">
                For faster access, save ShelfLife to your phone or tablet home screen.
              </p>
            </div>

            {/* Step 3 */}
            <div
              className="p-6 border border-border rounded-sm bg-white flex flex-col"
              data-testid="getting-started-step-3"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                  <ScanLine size={18} strokeWidth={2.5} className="text-ink" />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink/40">
                  Step 3
                </div>
              </div>
              <div className="font-display font-black text-xl leading-tight mb-2">
                Start Tracking Inventory
              </div>
              <p className="font-display text-sm text-ink/70 leading-relaxed flex-1">
                Scan products, enter expiration dates, and manage inventory from the floor.
              </p>
            </div>
          </div>

          {/* Install guide */}
          <div
            className="border-t border-border pt-12"
            data-testid="install-guide"
          >
            <div className="data-sku mb-3">Install Guide</div>
            <h3
              className="font-display font-black tracking-tight text-2xl sm:text-3xl mb-8"
              data-testid="install-guide-title"
            >
              Install ShelfLife on Your Device
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* iOS */}
              <div
                className="p-6 border border-border rounded-sm bg-white"
                data-testid="install-guide-ios"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                    <Apple size={18} strokeWidth={2.5} className="text-ink" />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-ink/40">
                      Platform
                    </div>
                    <div className="font-display font-black text-lg leading-tight">
                      iPhone / iPad
                    </div>
                  </div>
                </div>
                <ol className="space-y-3 font-display text-sm text-ink/80 leading-relaxed">
                  <li className="flex gap-3">
                    <span className="font-mono text-[11px] text-ink/40 mt-0.5 w-4 shrink-0">
                      1.
                    </span>
                    <span>Open ShelfLife in Safari.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-mono text-[11px] text-ink/40 mt-0.5 w-4 shrink-0">
                      2.
                    </span>
                    <span>Tap the Share button.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-mono text-[11px] text-ink/40 mt-0.5 w-4 shrink-0">
                      3.
                    </span>
                    <span>Scroll down and tap Add to Home Screen.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-mono text-[11px] text-ink/40 mt-0.5 w-4 shrink-0">
                      4.
                    </span>
                    <span>Tap Add.</span>
                  </li>
                </ol>
              </div>

              {/* Android */}
              <div
                className="p-6 border border-border rounded-sm bg-white"
                data-testid="install-guide-android"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                    <Smartphone size={18} strokeWidth={2.5} className="text-ink" />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-ink/40">
                      Platform
                    </div>
                    <div className="font-display font-black text-lg leading-tight">
                      Android
                    </div>
                  </div>
                </div>
                <ol className="space-y-3 font-display text-sm text-ink/80 leading-relaxed">
                  <li className="flex gap-3">
                    <span className="font-mono text-[11px] text-ink/40 mt-0.5 w-4 shrink-0">
                      1.
                    </span>
                    <span>Open ShelfLife in Chrome.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-mono text-[11px] text-ink/40 mt-0.5 w-4 shrink-0">
                      2.
                    </span>
                    <span>Tap the browser menu.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-mono text-[11px] text-ink/40 mt-0.5 w-4 shrink-0">
                      3.
                    </span>
                    <span>Tap Install App or Add to Home Screen.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-mono text-[11px] text-ink/40 mt-0.5 w-4 shrink-0">
                      4.
                    </span>
                    <span>Confirm installation.</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section
        id="contact"
        className="px-6 sm:px-10 py-20 border-t border-border"
        data-testid="contact-section"
      >
        <div className="max-w-5xl mx-auto">
          <div className="data-sku mb-3">Contact</div>
          <h2
            className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl mb-6"
            data-testid="contact-title"
          >
            Get in touch
          </h2>
          <p className="font-display text-base sm:text-lg text-ink/70 max-w-xl mb-8">
            Questions, feedback, or pilot inquiries? Reach the ShelfLife team directly.
          </p>
          <a
            href="mailto:info@blueharborlabs.app"
            className="inline-flex items-center gap-3 h-12 px-6 border border-border rounded-full font-display font-semibold text-sm tracking-tight bg-white hover:border-ink hover:-translate-y-0.5 transition-all duration-200 shadow-sm hover:shadow-md"
            data-testid="contact-email-link"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-brand" />
            info@blueharborlabs.app
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 sm:px-10 py-6 flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-ink/40">
        <div>ShelfLife · v1.0</div>
        <div>Built for fresh food retail</div>
      </footer>
    </div>
  );
}
